import { Controller } from "@hotwired/stimulus"
import { loadGoogleMaps } from "../lib/google_maps"
import { FreehandPen, closesTheGap } from "../lib/area_drawing"
import { showToast } from "../lib/toast"

// Company Settings → Timesheets → Locations (2026-10-09): the company's
// yards, gravel pits and offices, the same places the phone's Locations
// screen keeps. Each is a circle around a pin or an area drawn on the map
// (hold the mouse down with Draw on, or click corner by corner). Archived,
// never deleted. Saves through /timesheets/places.
const FT_PER_M = 3.28084
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c])

export default class extends Controller {
  static targets = ["list", "editor", "title", "name", "search", "map", "mapNote", "size", "sizeRow", "shapeRadio", "drawTools", "pencil", "error"]
  static values = { url: String, placeUrl: String, text: Object, editable: Boolean, startRadius: Number }

  connect() {
    this.places = []
    this.unit = "m"
    this.radii = [50, 100, 150, 200, 300, 500, 1000]
    this.showArchived = false
    this._load()
  }

  t(key, vars = {}) {
    let s = this.textValue[key] ?? ""
    if (typeof s === "object") s = (vars.count === 1 ? s.one : s.other) ?? ""
    for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`%{${k}}`, v)
    return s
  }

  async _load() {
    const data = await this._request("GET", this.urlValue)
    if (data) this._take(data)
  }

  _take(data) {
    this.places = data.places || []
    if (data.unit) this.unit = data.unit
    if (data.radii) this.radii = data.radii
    this._renderList()
  }

  _distance(meters) {
    return this.unit === "ft" ? `${Math.round(meters * FT_PER_M).toLocaleString()} ft` : `${Math.round(meters).toLocaleString()} m`
  }

  _renderList() {
    const active = this.places.filter((p) => p.active)
    const archived = this.places.filter((p) => !p.active)
    // data-search-row: the word search over the list (list_search_controller.js, K8-22) finds it by name.
    const row = (p) => `
      <div class="flex items-center justify-between gap-3 py-2 ${p.active ? "" : "opacity-60"}" data-search-row data-search-text="${esc(p.name)}">
        <div class="min-w-0">
          <div class="text-[13px] font-medium text-gray-900 truncate">${esc(p.name)}${p.active ? "" : ` <span class="text-gray-500 font-normal">· ${esc(this.t("archived"))}</span>`}</div>
          <div class="text-[13px] text-gray-500">${esc(p.shape ? this.t("places_drawn", { count: p.shape.length }) : this.t("places_radius", { distance: this._distance(p.radius_m) }))}</div>
        </div>
        ${this.editableValue ? `<div class="flex items-center gap-1 shrink-0">
          ${p.active ? `<button type="button" class="px-2 py-0.5 rounded-lg text-[13px] text-brand hover:bg-gray-50 cursor-pointer" data-id="${p.id}" data-action="time-places#edit">${esc(this.t("places_edit"))}</button>` : ""}
          <button type="button" class="px-2 py-0.5 rounded-lg text-[13px] text-gray-600 hover:bg-gray-50 cursor-pointer" data-id="${p.id}" data-action="time-places#toggleActive">${esc(p.active ? this.t("archive") : this.t("restore"))}</button>
        </div>` : ""}
      </div>`
    let html = active.length ? active.map(row).join("") : `<p class="py-2 text-[13px] text-gray-500">${esc(this.t("places_empty"))}</p>`
    if (archived.length) {
      html += `<button type="button" class="mt-1 text-[13px] text-gray-500 hover:text-gray-700 underline cursor-pointer" data-action="time-places#toggleArchived">${esc(this.showArchived ? this.t("places_hide_archived") : this.t("show_archived", { count: archived.length }))}</button>`
      if (this.showArchived) html += archived.map(row).join("")
    }
    this.listTarget.innerHTML = `<div class="divide-y divide-gray-100">${html}</div>`
  }

  toggleArchived() {
    this.showArchived = !this.showArchived
    this._renderList()
  }

  async toggleActive(e) {
    const place = this.places.find((p) => p.id === Number(e.currentTarget.dataset.id))
    if (!place) return
    const data = await this._request("PATCH", this.placeUrlValue.replace(":id", place.id), { active: !place.active })
    if (data) {
      this._take(data)
      showToast(this.t("places_saved"))
    }
  }

  // ── The editor ──────────────────────────────────────────────────────

  add() { this._open(null) }

  edit(e) { this._open(this.places.find((p) => p.id === Number(e.currentTarget.dataset.id)) || null) }

  _open(place) {
    this.editing = place
    this.editorTarget.hidden = false
    this._error(null)
    this.titleTarget.textContent = place ? place.name : this.t("places_new")
    this.nameTarget.value = place?.name || ""
    this.sizeTarget.innerHTML = this.radii.map((m) => `<option value="${m}">${esc(this._distance(m))}</option>`).join("")
    // A new yard starts at the company's starting size for new locations (K13-9).
    const radius = place ? Number(place.radius_m) : (this.startRadiusValue || 304.8)
    if (!this.radii.includes(radius)) this.sizeTarget.insertAdjacentHTML("beforeend", `<option value="${radius}">${esc(this._distance(radius))}</option>`)
    this.sizeTarget.value = String(radius)
    const drawn = !!place?.shape
    this.shapeRadioTargets.forEach((r) => { r.checked = r.value === (drawn ? "draw" : "circle") })
    this._pin = place ? { lat: Number(place.latitude), lng: Number(place.longitude) } : null
    this._startShape = drawn ? place.shape.map(([lat, lng]) => ({ lat, lng })) : []
    this.editorTarget.scrollIntoView?.({ behavior: "smooth", block: "nearest" })
    loadGoogleMaps()
      .then(() => this._showMap())
      .catch(() => { this.mapTarget.hidden = true; this.mapNoteTarget.hidden = false })
  }

  cancel() {
    this.editorTarget.hidden = true
    this._pen?.enable(false)
    this.editing = null
  }

  _drawnMode() {
    return this.shapeRadioTargets.find((r) => r.checked)?.value === "draw"
  }

  _showMap() {
    const g = window.google.maps
    if (!this._map) {
      this._map = new g.Map(this.mapTarget, {
        center: { lat: 39.5, lng: -98.35 }, zoom: 4, mapTypeId: "hybrid",
        streetViewControl: false, mapTypeControl: false, fullscreenControl: false, gestureHandling: "greedy"
      })
      this._marker = new g.Marker({ map: this._map, draggable: true, visible: false })
      this._marker.addListener("dragend", () => {
        const p = this._marker.getPosition()
        this._setPin(p.lat(), p.lng())
      })
      this._map.addListener("click", (ev) => {
        if (this._drawnMode() && this._drawing) return this._addCorner(ev.latLng)
        this._setPin(ev.latLng.lat(), ev.latLng.lng())
      })
      this._circle = new g.Circle({ map: this._map, clickable: false, strokeWeight: 2, strokeColor: "#2563eb", fillColor: "#2563eb", fillOpacity: 0.15 })
      this._corners = new g.MVCArray([])
      this._polygon = new g.Polygon({ map: this._map, paths: new g.MVCArray([ this._corners ]), editable: true,
        strokeColor: "#2563eb", strokeWeight: 2, fillColor: "#2563eb", fillOpacity: 0.15 })
      this._pen = new FreehandPen({ container: this.mapTarget, map: this._map,
        onTap: (latLng) => this._addCorner(latLng),
        onStroke: (points) => { this._setCorners(points); this._setDrawing(false) } })
      if (this.hasSearchTarget && g.places?.Autocomplete) {
        const auto = new g.places.Autocomplete(this.searchTarget, { fields: ["geometry", "name"] })
        auto.addListener("place_changed", () => {
          const found = auto.getPlace()
          if (!found?.geometry) return
          this._setPin(found.geometry.location.lat(), found.geometry.location.lng())
          this._map.setCenter(found.geometry.location)
          this._map.setZoom(17)
        })
        this.searchTarget.addEventListener("keydown", (e) => { if (e.key === "Enter") e.preventDefault() })
      }
    }
    this._setCorners(this._startShape)
    if (this._pin) {
      this._marker.setPosition(this._pin)
      this._marker.setVisible(true)
      this._map.setCenter(this._pin)
      this._map.setZoom(17)
    } else {
      this._marker.setVisible(false)
    }
    this._frameDrawing()
    this._setDrawing(this._drawnMode() && this._corners.getLength() < 3)
    this._redraw()
  }

  _setPin(lat, lng) {
    this._pin = { lat: Number(lat.toFixed(7)), lng: Number(lng.toFixed(7)) }
    this._marker.setPosition(this._pin)
    this._marker.setVisible(true)
    this._redraw()
  }

  shapeChanged() {
    this._setDrawing(this._drawnMode() && this._corners?.getLength() < 3)
    this._redraw()
  }

  sizeChanged() { this._redraw() }

  togglePencil() { this._setDrawing(!this._drawing) }

  clearDrawing() {
    this._corners?.clear()
    this._setDrawing(true)
    this._redraw()
  }

  _setDrawing(on) {
    this._drawing = !!on
    this._pen?.enable(this._drawing)
    this._polygon?.setOptions?.({ clickable: !this._drawing })
    this._map?.setOptions?.({ draggableCursor: this._drawing ? "crosshair" : null })
    if (this.hasPencilTarget) {
      this.pencilTarget.textContent = this._drawing ? this.t("places_pencil_on") : this.t("places_pencil")
      this.pencilTarget.classList.toggle("bg-brand", this._drawing)
      this.pencilTarget.classList.toggle("text-white", this._drawing)
    }
  }

  _addCorner(latLng) {
    if (!this._corners || this._corners.getLength() >= 40) return
    if (closesTheGap(this._corners.getArray(), latLng, this._map?.getZoom?.())) return this._setDrawing(false)
    this._corners.push(latLng)
  }

  _setCorners(points) {
    if (!this._corners) return
    this._corners.clear()
    points.slice(0, 40).forEach((p) => this._corners.push(new window.google.maps.LatLng(p.lat, p.lng)))
  }

  _frameDrawing() {
    if (!this._corners || this._corners.getLength() < 3) return
    const bounds = new window.google.maps.LatLngBounds()
    this._corners.getArray().forEach((p) => bounds.extend(p))
    this._map.fitBounds(bounds, 48)
  }

  _redraw() {
    const drawn = this._drawnMode()
    this.sizeRowTarget.hidden = drawn
    this.drawToolsTarget.hidden = !drawn
    if (!this._circle) return
    this._circle.setVisible(!drawn && !!this._pin)
    if (this._pin) { this._circle.setCenter(this._pin); this._circle.setRadius(Number(this.sizeTarget.value)) }
    this._polygon.setVisible(drawn)
  }

  async save() {
    const name = this.nameTarget.value.trim()
    if (!name) return this._error(this.t("places_name_needed"))
    const drawn = this._drawnMode()
    const body = { name }
    if (drawn) {
      const corners = this._corners ? this._corners.getArray().map((p) => [Number(p.lat().toFixed(7)), Number(p.lng().toFixed(7))]) : []
      if (corners.length < 3) return this._error(this.t("places_draw_need_web"))
      body.shape = corners
    } else {
      if (!this._pin) return this._error(this.t("places_pin_needed"))
      Object.assign(body, { latitude: this._pin.lat, longitude: this._pin.lng, radius_m: Number(this.sizeTarget.value) })
      if (this.editing?.shape) body.shape = [] // back to the circle
    }
    const url = this.editing ? this.placeUrlValue.replace(":id", this.editing.id) : this.urlValue
    const data = await this._request(this.editing ? "PATCH" : "POST", url, body)
    if (!data) return
    this._take(data)
    this.cancel()
    showToast(this.t("places_saved"))
  }

  _error(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message || ""
    this.errorTarget.hidden = !message
  }

  async _request(method, url, body) {
    try {
      const res = await fetch(url, {
        method,
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
          "Accept": "application/json",
          ...(body ? { "Content-Type": "application/json" } : {})
        },
        body: body ? JSON.stringify(body) : undefined
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || this.t("places_failed"))
      return data
    } catch (err) {
      if (this.editing !== undefined && this.hasEditorTarget && !this.editorTarget.hidden) this._error(err.message)
      else showToast(err.message, "alert")
      return null
    }
  }
}
